import { useId } from "react";
import { twMerge } from "tailwind-merge";
import { buttonClasses } from "../../../Styles/Buttons";
import { ButtonGroup } from "../../ButtonGroup";
import { RadioButtonGroupOption } from "../RadioButtonGroup";

interface ButtonRadioButtonGroupProps {
  name: string;
  value: string | number;
  onChange: (e: { target: { value: string | number } }) => void;
  options: RadioButtonGroupOption[];
  error?: string;
  disabled?: boolean;
  /** Tighter padding and intrinsic width (for table quick filters). */
  compact?: boolean;
}

const ButtonRadioButton = ({
  name,
  value,
  checked,
  onChange,
  option,
  disabled,
  groupPosition,
  compact,
}: {
  name: string;
  value: string | number;
  checked: boolean;
  onChange: (value: string | number) => void;
  option: RadioButtonGroupOption;
  disabled?: boolean;
  groupPosition?: "left" | "middle" | "right";
  compact?: boolean;
}) => {
  const id = useId();
  const selected = checked;
  const kind = selected ? (option.selectedKind ?? "secondary") : "secondary";

  return (
    <label
      className={buttonClasses({
        kind,
        pressed: selected,
        groupPosition,
        compact,
        className: twMerge(
          "flex min-w-0 cursor-pointer items-center justify-center text-xs",
          "space-x-0",
          compact ? "w-auto" : "w-full",
          option.labelClassName,
          disabled && "pointer-events-none opacity-50",
        ),
      })}
      htmlFor={id}
      aria-checked={selected}
    >
      <input
        type="radio"
        name={name}
        value={String(option.value)}
        checked={selected}
        className="sr-only"
        id={id}
        onChange={() => onChange(option.value)}
        disabled={disabled}
      />
      <span className="inline-flex items-center justify-center gap-2">
        {option.icon}
        <span>{option.label}</span>
      </span>
    </label>
  );
};

export const ButtonRadioButtonGroup = ({
  name,
  value,
  onChange,
  options,
  error,
  disabled,
  compact,
}: ButtonRadioButtonGroupProps) => {
  return (
    <ButtonGroup
      stripChildBoxShadow={false}
      className={twMerge("inline-flex w-auto flex-row", !compact && "flex w-full min-w-0 *:min-w-0 *:flex-1")}
    >
      {options.map((option) => (
        <ButtonRadioButton
          checked={String(value) === String(option.value)}
          key={option.value}
          name={name}
          value={value}
          onChange={(value) => onChange({ target: { value } })}
          option={option}
          disabled={disabled}
          compact={compact}
        />
      ))}
    </ButtonGroup>
  );
};
